import { debounce }                                 from "lodash";
import { useRouter }                                from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import useSWR                                       from "swr";
import useSWRInfinite                               from "swr/infinite";

import { useGetLoggedIn, useGetProfileData }    from "_common/hooks/auth";
import { useSetLoginPopup }                     from "_common/hooks/global";
import { useSetPipMode }                        from "_common/hooks/player";
import Toast                                    from "_common/toast";
import { getCommentsOnFeed, postCommentOnFeed } from "_services/api/feed";
import remoteConfigInstance                     from "_services/firebase/fetchRemoteConfig";

import Presentation from "./presentation";

const fetchEmotesList = async () => {

	const remoteConfigEmotes = await remoteConfigInstance.getData ( "sportx", "emotes_list_v2" );
	const emotes             = mapEmotes ( remoteConfigEmotes );

	return emotes;

};

const mapEmotes = data => {
	return data.reduce ( ( acc, item ) => {
		acc[item.name] = item.url;

		return acc;
	}, {} );
};

const fetchComments = async params => {
	const { id, pageNo } = params;

	const res = await getCommentsOnFeed ( { feedId: parseInt ( id ), pageNo, pageSize: 10 } );

	return res;
};

const component = ( { videoId, isOpen, commentCount: commentCountProps, onClose = () => { }, collapse, toggleCollapse } ) => {

	const router                                    = useRouter ();
	const rightSideRef                              = useRef ();
	const setLoginPopup                             = useSetLoginPopup ();
	const userProfile                               = useGetProfileData ();
	const isLoggedIn                                = useGetLoggedIn ();
	const togglePipModeVideo                        = useSetPipMode ();
	const [ openCommentModal, setOpenCommentModal ] = useState ( false );

	useEffect ( () => {
		setOpenCommentModal ( isOpen );
	}, [ isOpen ] );

	const { currentTime } = router.query;

	const { data: comments, error: commentError, size: commentSize, setSize: setCommentSize } = useSWRInfinite (
		index => {
			return { key: "videos-comments", pageNo: index + 1, id: videoId };
		},
		fetchComments,
		{ revalidateFirstPage: false }
	);

	const { data: emotesList } = useSWR ( { key: "emote-list" }, fetchEmotesList, { fallbackData: {} } );

	// state for infinite call logic
	let __comments                 = [].concat.apply ( [], comments );
	const isLoadingInitialComments = !comments && !commentError;
	const isCommentsEmpty          = comments?.[commentSize - 1]?.length === 0;
	const isLoadingMoreComments    = isLoadingInitialComments || ( commentSize > 0 && comments && typeof comments[commentSize - 1] === "undefined" );

	const [ commentInput, setCommentInput ]      = useState ( "" );
	const [ ownCommentsList, setOwnCommentList ] = useState ( [] );
	const [ commentCount, setCommentCount ]      = useState ( 0 );
	const [ playlist, setPlaylist ]              = useState ( [] );

	const [ displayEmoji, setDisplayEmoji ] = useState ( false );
	const [ focusState, setFocusState ]     = useState ( false );
	const textInput                         = useRef ( null );

	useEffect ( () => {
		togglePipModeVideo ( {
			status     : false,
			sessionId  : undefined,
			sessionSrc : undefined
		} );
	}, [] );

	useEffect ( () => {
		setCommentCount ( commentCountProps );
		setOwnCommentList ( [] );
	}, [ videoId ] );

	const currentTabParams = {
		data       : [ ...ownCommentsList, ...__comments ],
		isFetching : isLoadingMoreComments,
		isDepleted : isCommentsEmpty,
		pageNo     : commentSize
	};

	const sendCommentMessage = async () => {
		if ( !isLoggedIn )
			return setLoginPopup ( true );

		if ( !commentInput ) return false;

		try {
			const res = await postCommentOnFeed ( {
				commentMessage : commentInput,
				feedId         : parseInt ( videoId )
			} );

			setOwnCommentList ( prevState => [ {
				id               : res.comment_id,
				commentMessage   : commentInput,
				sportsFanDetails : {
					name  : userProfile.name,
					photo : userProfile.photo,
					id    : userProfile.id
				},
				sportsFanId: userProfile.id
			}, ...prevState ] );

			setCommentCount ( res.reaction_count );
		} catch ( err ) {
			Toast.error ( err.displayMsg );
		}
		setCommentInput ( "" );
	};

	const handleSendClick = async e => {

		e.preventDefault ();
		sendCommentMessage ();
	};

	const handleKeydown = async e => {

		if ( e.key === "Enter" || e.key === "NumpadEnter" ) {
			e.preventDefault ();
			sendCommentMessage ();
		}
	};

	const throttledCommentToPost = useCallback ( debounce ( handleKeydown, 500 ), [ commentInput ] );

	const throttledCommentToClick = useCallback ( debounce ( handleSendClick, 500 ), [ commentInput ] );

	const handleOnCommentChange = e => {
		setCommentInput ( e.target.value );
	};

	const fetchMoreComments = () => {
		setCommentSize ( commentSize + 1 );
	};

	/* For Showing Emoji in text */
	const handleEmojiSelect = useCallback (
		e => {
			/*
        Splitting the id  on (‘-’), before adding ‘0x’ to each element
      */
			let sym        = e.unified.split ( "-" );
			let codesArray = [];

			/*
        Here, unified is in Unicode (hex) so
        we need to add ‘0x’ in the front of the code for it to become an emoji
      */
			sym.forEach ( el => codesArray.push ( "0x" + el ) );
			let emoji = String.fromCodePoint ( ...codesArray );

			setCommentInput ( commentInput + " " + emoji );
			textInput?.current?.focus ();
			setFocusState ( true );
		},
		[ commentInput, focusState, textInput ]
	);

	/* For Toggle Emoji Container */
	const handleToggleEmoji = () => {
		if ( !isLoggedIn ) {
			setLoginPopup ( true );

			return;
		}
		if ( displayEmoji && focusState )
			textInput?.current?.focus ();
		setDisplayEmoji ( prev => !prev );
	};

	const toggleFocusState = () => {
		if ( displayEmoji && focusState )
			textInput?.current?.focus ();
		setFocusState ( prev => !prev );
	};

	return (
		<Presentation
			collapse = { collapse }
			commentCount = { commentCount }
			commentInput = { commentInput }
			currentTabParams = { currentTabParams }
			currentTime = { currentTime }
			displayEmoji = { displayEmoji }
			emotesList = { emotesList }
			fetchComments = { fetchMoreComments }
			focusState = { focusState }
			handleEmojiSelect = { handleEmojiSelect }
			handleOnCommentChange = { handleOnCommentChange }
			handleSendClick = { throttledCommentToClick }
			handleToggleEmoji = { handleToggleEmoji }
			onClose = { onClose }
			openCommentModal = { openCommentModal }
			playlist = { playlist }
			rightSideRef = { rightSideRef }
			setOpenCommentModal = { setOpenCommentModal }
			setPlaylist = { setPlaylist }
			textInput = { textInput }
			throttledCommentToPost = { throttledCommentToPost }
			toggleCollapse = { toggleCollapse }
			toggleFocusState = { toggleFocusState }
		/>
	);
};

export default component;
